import React, { useEffect, useState } from "react";
import { Modal, Tooltip, Button, Segmented, ConfigProvider } from "antd";
import { workflowContainerEmptyById } from "../../../../../src/libs/apis";
import { WorkflowContainerEmptyI } from "../../../../../src/libs/interfaces";
import { Box, Grid, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  FundViewOutlined,
  ProfileOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { COLOR_PRIMARY, COLOR_SECONDARY } from "../../../../../src/libs/constants";
import { OneCard, OneVideo } from "../../../../../src/libs/shared";
import { useSnackbar } from "@components";

const noShow = [
  "id",
  "client",
  "producer",
  "farm",
  "zone",
  "balerCode",
  "box",
  "brand",
  "container",
  "clientId",
  "producerId",
  "farmId",
  "magap",
  "boxId",
  "brandId",
  "startProcess",
  "containerArrival",
  "containerOpen",
  "startLoading",
  "videoContenedorVacio",
  "videoPisoExterno",
  "gancho",
  "ganchoCi",
  "calidad",
  "calidadCi",
  "date",
  "driver",
  "companyTransport",
  "dateVideoContenedorVacio",
  "numKit",
  "observation",
  "timeStampSave",
];

const titles = {
  eir: "Eir",
  ciConductor: "Identificación Conductor",
  placaCabezal: "Placa Cabezal",
  etiquetaVerificadoraPuerta: "Et. Verificadora Puerta",
  etiquetasVentoleras: "Etiquetas Ventoleras",
  etiquetasPanelExteriorExp: "Et. Panel Ext. Exportador",
  etiquetasPanelExterior: "Et. Panel Ext. Verificadora",
  etiquetasPanelInterior: "Et. Panel Interior",
  plastico: "Plástico",
  botellaRastreo: "Botella Rastreo",
  numRastreoSatelital: "Número Rastreo",
  etiquetaExporPuerta: "Etiqueta Puerta Exportador",
  macroEtiquetasVentoleras: "Macro Etiquetas Ventoleras",
  macroEtiquetasVentolerasAdicional: "Et. Ventoleras Adicional",
  numKit: "Número Kit",
  placa: "Placa Vehicular",
} as any;

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
}

interface ShowContainerPropsI {
  id: number;
  disabled?: boolean; // Añadir propiedad para deshabilitar en modo offline
}

export const ShowContainer = ({ id }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer open={open} onClose={handleClose} id={id} />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

export const ModalContainer = ({ open, onClose, id }: ModalContainerPropsI) => {
  const [isLoading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<WorkflowContainerEmptyI>({
    container: "",
  } as WorkflowContainerEmptyI);
  const [cards, setCards] = useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = useState<OnePhotoI[]>([]);
  const [lasFotos, setLasFotos] = useState<string[]>([]);
  const [tab, setTab] = useState<string | number>("info");

  useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as WorkflowContainerEmptyI);
      setLoading(true);
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainer } = await workflowContainerEmptyById(id);
      if (!dataContainer) return;
      setData(dataContainer);
      const arrKeys = Object.keys(dataContainer).filter(
        (el) => !noShow.includes(el)
      ) as Array<keyof WorkflowContainerEmptyI>;

      const keysParents = arrKeys
        .filter((el) => !el.includes("Photo"))
        .filter((el) => !el.includes("photo"))
        .filter((el) => !el.includes("video"));

      const arrCards = [];
      const arrPhotosTotal: string[] = [];

      for (const x of keysParents) {
        const obj: OneCardI = { title: titles[x] ?? x } as OneCardI;
        const arrPhotos = [];
        for (const y of arrKeys) {
          if (y !== x && y.includes(x)) {
            arrPhotos.push({
              src: dataContainer[y],
              alt: y,
            });
            arrPhotosTotal.push(dataContainer[y] as any);
          }
        }
        obj.photos = arrPhotos;
        obj.value = dataContainer[x] as string;
        arrCards.push(obj);
      }
      const objContainer = {
        title: "Contenedor",
        photos: [{ src: dataContainer["siglasEPhoto"], alt: "Contenedor" }],
      } as OneCardI;

      arrCards.unshift(objContainer);
      arrPhotosTotal.push(dataContainer["siglasEPhoto"] as any);

      const sellosArriboVerificadora = {
        title: "Sellos Arribo Verificadora",
        photos: [
          {
            src: dataContainer["etiquetaVerificadoraPuertaPhoto"],
            alt: "Etiqueta Puerta",
          },
          {
            src: dataContainer["plasticoPhoto"],
            alt: "Sello Plástico",
          },
          {
            src: dataContainer["botellaRastreoPhoto"],
            alt: "Sello Rastreo",
          },
          {
            src: dataContainer["numRastreoSatelitalPhoto"],
            alt: "Número de Rastreo",
          },
        ],
      };
      arrCards.push(sellosArriboVerificadora);
      arrPhotosTotal.push(
        dataContainer["etiquetaVerificadoraPuertaPhoto"] as any
      );
      arrPhotosTotal.push(dataContainer["plasticoPhoto"] as any);
      arrPhotosTotal.push(dataContainer["botellaRastreoPhoto"] as any);
      arrPhotosTotal.push(dataContainer["numRastreoSatelitalPhoto"] as any);

      const objEstInt = {
        title: "Estructura Interior",
        noLine: true,
        photos: [
          {
            src: dataContainer["paredInternaLadoDerechoPhoto"],
            alt: "Pared Lado Derecho",
            showAlt: true,
          },
          {
            src: dataContainer["paredInternaLadoIzquierdoPhoto"],
            alt: "Pared Lado Izquierdo",
            showAlt: true,
          },
          {
            src: dataContainer["techoInternoPhoto"],
            alt: "Techo",
            showAlt: true,
          },
          {
            src: dataContainer["pisoInternoPhoto"],
            alt: "Piso",
            showAlt: true,
          },
        ],
      };
      arrCards.push(objEstInt);
      arrPhotosTotal.push(dataContainer["paredInternaLadoDerechoPhoto"] as any);
      arrPhotosTotal.push(
        dataContainer["paredInternaLadoIzquierdoPhoto"] as any
      );
      arrPhotosTotal.push(dataContainer["techoInternoPhoto"] as any);
      arrPhotosTotal.push(dataContainer["pisoInternoPhoto"] as any);

      const objPanInt = {
        title: "Panel Interno",
        photos: [
          {
            src: dataContainer["panelInternoPhoto1"],
            alt: "panelInternoPhoto1",
          },
          {
            src: dataContainer["panelInternoPhoto2"],
            alt: "panelInternoPhoto2",
          },
          {
            src: dataContainer["panelInternoPhoto3"],
            alt: "panelInternoPhoto3",
          },
          {
            src: dataContainer["panelInternoPhoto4"],
            alt: "panelInternoPhoto4",
          },
        ],
      };
      arrCards.push(objPanInt);
      arrPhotosTotal.push(dataContainer["panelInternoPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["panelInternoPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["panelInternoPhoto3"] as any);
      arrPhotosTotal.push(dataContainer["panelInternoPhoto4"] as any);

      const objParchesInt = {
        title: "Parches Interno",
        photos: [
          { src: dataContainer["parchesPhoto1"], alt: "parchesPhoto1" },
          { src: dataContainer["parchesPhoto2"], alt: "parchesPhoto2" },
          { src: dataContainer["parchesPhoto3"], alt: "parchesPhoto3" },
        ],
      };
      arrCards.push(objParchesInt);
      arrPhotosTotal.push(dataContainer["parchesPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["parchesPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["parchesPhoto3"] as any);

      const objEstExt = {
        title: "Estructura Exterior",
        noLine: true,
        photos: [
          {
            src: dataContainer["paredExternaLadoDerechoPhoto"],
            alt: "Pared Lado Derecho",
            showAlt: true,
          },
          {
            src: dataContainer["paredExternaLadoIzquierdoPhoto"],
            alt: "Pared Lado Izquierdo",
            showAlt: true,
          },
          {
            src: dataContainer["panelExteriorPhoto"],
            alt: "Panel",
            showAlt: true,
          },
          {
            src: dataContainer["techoExteriorPhoto"],
            alt: "Techo",
            showAlt: true,
          },
          {
            src: dataContainer["pisoDerechoExteriorPhoto"],
            alt: "Piso Derecho",
            showAlt: true,
          },
          {
            src: dataContainer["pisoIzquierdoExteriorPhoto"],
            alt: "Piso Izquierdo",
            showAlt: true,
          },
        ],
      } as OneCardI;

      arrCards.push(objEstExt);
      arrPhotosTotal.push(dataContainer["paredExternaLadoDerechoPhoto"] as any);
      arrPhotosTotal.push(
        dataContainer["paredExternaLadoIzquierdoPhoto"] as any
      );
      arrPhotosTotal.push(dataContainer["panelExteriorPhoto"] as any);
      arrPhotosTotal.push(dataContainer["techoExteriorPhoto"] as any);
      arrPhotosTotal.push(dataContainer["pisoDerechoExteriorPhoto"] as any);
      arrPhotosTotal.push(dataContainer["pisoIzquierdoExteriorPhoto"] as any);

      const objParchesExt = {
        title: "Parches Externo",
        photos: [
          { src: dataContainer["parchesExtPhoto1"], alt: "parchesExtPhoto1" },
          { src: dataContainer["parchesExtPhoto2"], alt: "parchesExtPhoto2" },
          { src: dataContainer["parchesExtPhoto3"], alt: "parchesExtPhoto3" },
        ],
      };
      arrCards.push(objParchesExt);
      arrPhotosTotal.push(dataContainer["parchesExtPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["parchesExtPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["parchesExtPhoto3"] as any);

      const arrVideos = [
        {
          src: dataContainer.videoContenedorVacio,
          alt: "Video Contenedor Vacio",
        },
        // {
        //   src: dataContainer.videoPisoExterno,
        //   alt: "Video Piso Externo",
        // },
      ];

      setCardVideos(arrVideos);
      setCards(arrCards);
      setLasFotos(arrPhotosTotal);
      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  const d = data as any;
  const sections: { title: string; fields: [string, any][] }[] = [
    {
      title: "Datos generales",
      fields: [
        ["N° Contenedor", d.container],
        ["Exportador", d.client],
        ["MAGAP", d.magap],
        ["Productor", d.producer],
        ["Hacienda", d.farm],
        ["Zona", d.zone],
        ["Código empacadora", d.balerCode],
        ["Marca", d.brand],
        ["Tipo de caja", d.box],
        ["Número kit", d.numKit],
      ],
    },
    {
      title: "Transporte",
      fields: [
        ["Conductor", d.driver],
        ["Identificación conductor", d.ciConductor],
        ["Placa", d.placa],
        ["Compañía de transporte", d.companyTransport],
      ],
    },
    {
      title: "Calidad - Gancho",
      fields: [
        ["Calidad", d.calidad],
        ["Identificación calidad", d.calidadCi],
        ["Gancho", d.gancho],
        ["Identificación gancho", d.ganchoCi],
      ],
    },
    {
      title: "Sellos y etiquetas",
      fields: [
        "eir",
        "etiquetaVerificadoraPuerta",
        "etiquetaExporPuerta",
        "plastico",
        "botellaRastreo",
        "numRastreoSatelital",
        "etiquetasVentoleras",
        "macroEtiquetasVentoleras",
        "macroEtiquetasVentolerasAdicional",
        "etiquetasPanelExteriorExp",
        "etiquetasPanelExterior",
        "etiquetasPanelInterior",
      ].map((k) => [titles[k] ?? k, d[k]] as [string, any]),
    },
    {
      title: "Horas de proceso",
      fields: [
        ["Inicio de proceso", d.startProcess],
        ["Llegada del contenedor", d.containerArrival],
        ["Apertura del contenedor", d.containerOpen],
        ["Inicio de carga", d.startLoading],
      ],
    },
  ];

  const photosCount = cards.reduce(
    (acc, c) => acc + c.photos.filter((p) => p.src).length,
    0
  );

  return (
    <Modal
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "white" }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,.15)",
              fontSize: 18,
            }}
          >
            <FundViewOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
              Contenedor Vacío {data.container ? `N° ${data.container}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>
              {d.client || "Información, fotos y videos del registro"}
            </Typography>
          </Box>
        </Box>
      }
      footer={null}
      loading={isLoading}
      open={open}
      onCancel={onClose}
      width={900}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      closeIcon={<span style={{ color: "white" }}>✕</span>}
      styles={{
        header: {
          background: COLOR_PRIMARY,
          margin: 0,
          padding: "16px 24px",
          borderRadius: "8px 8px 0 0",
        },
        content: { padding: 0, borderRadius: 8, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e6ebf2" }}>
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                itemSelectedBg: COLOR_PRIMARY,
                itemSelectedColor: "#fff",
                itemColor: COLOR_PRIMARY,
                trackBg: "#e6ebf2",
              },
            },
          }}
        >
          <Segmented
            block
            size="large"
            value={tab}
            onChange={setTab}
            options={[
              { value: "info", icon: <ProfileOutlined />, label: "Información" },
              { value: "photos", icon: <PictureOutlined />, label: `Fotos (${photosCount})` },
              { value: "videos", icon: <VideoCameraOutlined />, label: "Videos" },
            ]}
          />
        </ConfigProvider>
      </Box>

      <Box sx={{ height: 520, overflowY: "auto", p: 2.5 }}>
        {tab === "info" && (
          <Grid container spacing={2.5}>
            {sections.map((sec) => (
              <Grid item xs={12} key={sec.title}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: COLOR_SECONDARY,
                    pb: 0.75,
                    mb: 1.5,
                    borderBottom: "1px solid #e6ebf2",
                  }}
                >
                  {sec.title}
                </Typography>
                <Grid container spacing={2}>
                  {sec.fields.map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={`${sec.title}-${label}`}>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 500, wordBreak: "break-word" }}>
                        {value || "—"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  color: COLOR_SECONDARY,
                  pb: 0.75,
                  mb: 1.5,
                  borderBottom: "1px solid #e6ebf2",
                }}
              >
                Observación
              </Typography>
              <Typography sx={{ fontSize: 14.5, whiteSpace: "pre-wrap" }}>
                {d.observation || "—"}
              </Typography>
            </Grid>
          </Grid>
        )}

        {tab === "photos" && (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              width: "100%",
            }}
          >
            {cards.map((el) => (
              <OneCard data={el} key={`${el.title}-card`} lasFotos={lasFotos} />
            ))}
          </Box>
        )}

        {tab === "videos" && (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              width: "100%",
            }}
          >
            {cardVideos.map((el) => (
              <OneVideo {...el} key={`${el.alt}-video`} />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
};
